import { Alert, Badge, Card, Group, Spoiler, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useCaseConsents, type CaseConsentEntry } from '../api/consent';

interface Props {
  caseId: string;
  orgId: string;
  productId: string;
  locale?: string;
}

function StatusBadge({ entry }: { entry: CaseConsentEntry }) {
  const status = entry.answer?.status;
  if (status === 'granted')
    return (
      <Badge color="green" variant="light">
        Granted
      </Badge>
    );
  if (status === 'declined')
    return (
      <Badge color="red" variant="light">
        Declined
      </Badge>
    );
  if (status)
    return (
      <Badge color="gray" variant="light">
        {status}
      </Badge>
    );
  return (
    <Badge color="gray" variant="outline">
      Not answered
    </Badge>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

/**
 * Case-detail consent panel (#1079). Lists every consent configured for the
 * case's org+product with the exact wording and the patient's answer. Read
 * only and ungated — consent wording/answers are not PHI.
 */
export function CaseConsentSection({ caseId, orgId, productId, locale }: Props) {
  const query = useCaseConsents(caseId, orgId, productId, locale);
  const consents = query.data?.consents ?? [];

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group gap="xs" align="center">
          <Title order={5}>Consent</Title>
          {query.data && <Badge variant="light">{consents.length}</Badge>}
        </Group>

        {query.isLoading ? (
          <Text c="dimmed">Loading consent…</Text>
        ) : query.isError ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Could not load consent.
          </Alert>
        ) : consents.length === 0 ? (
          <Text c="dimmed">No consents configured for this case.</Text>
        ) : (
          <Stack gap="lg">
            {consents.map((entry) => {
              const body = entry.answer?.text_version.body || entry.wording;
              return (
                <div key={`${entry.consent_type_id}-${entry.configured}`}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <div>
                      <Text fw={500}>{entry.display_name}</Text>
                      {entry.legal_basis && (
                        <Text size="xs" c="dimmed">
                          Legal basis: {entry.legal_basis}
                        </Text>
                      )}
                      {!entry.configured && (
                        <Text size="xs" c="orange">
                          No longer configured
                        </Text>
                      )}
                    </div>
                    <StatusBadge entry={entry} />
                  </Group>
                  {entry.answer && (
                    <Text size="xs" c="dimmed" mt={4}>
                      Answered {formatDate(entry.answer.captured_at)}
                      {entry.answer.recorded_by ? ` by ${entry.answer.recorded_by}` : ''}
                    </Text>
                  )}
                  {body && (
                    <Spoiler
                      mt="xs"
                      maxHeight={0}
                      showLabel="Show wording"
                      hideLabel="Hide wording"
                    >
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {body}
                      </Text>
                    </Spoiler>
                  )}
                </div>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
